import type { DialogComponentProps } from '@brightdrop/bd-ui';
import { AlertBanner } from '@brightdrop/bd-ui';
import type {
  MRT_RowSelectionState,
  MRT_TableOptions,
  TableToolbarProps,
} from '@brightdrop/table-ui';
import { Table, TableFilterDisplayMode } from '@brightdrop/table-ui';
import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetEligibleVehiclesMutation } from '~/api/generated-api';
import BDFilterModal, {
  BDFilterModalType,
} from '~/common/components/BDFilter/BDFilterModal';
import { useOrgId } from '~/common/hooks/useOrgId';
import { useLocalizedIntegerCount } from '~/common/localization/useLocalizedFormatters';
import { getFleetFilterConfig } from '~/features/catalog/shared/components/eligibleVehiclesView/filter/eligibleVehiclesFilter.config';
import {
  ELIGIBLE_VEHICLES_FILTER_EMPTY_STATE,
  type EligibleVehiclesFilterCriteria,
} from '~/features/catalog/shared/components/eligibleVehiclesView/filter/eligibleVehiclesFilter.model';
import { useCatalogFleets } from '~/features/catalog/shared/hooks/useCatalogFleets';

import useStyles from './VehiclesTable.styles';
import type {
  EligibleVehicleRow,
  VehicleSelectionConfig,
} from './vehicleSelection.types';

const PAGE_SIZE = 10;
// VIN is always present on a vehicle; the spec just doesn't mark it required.
const getVehicleRowId = (row: EligibleVehicleRow) => row.vin as string;
const DEFAULT_FLEET_FILTER: EligibleVehiclesFilterCriteria = {
  ...ELIGIBLE_VEHICLES_FILTER_EMPTY_STATE,
};

export type VehiclesTableProps = VehicleSelectionConfig & {
  selectedVehicleIds: string[];
  onSelectionChange: (vehicleIds: string[]) => void;
  onTotalChange?: (total: number) => void;
  /** Opened from "View vehicles": rows are listed without selection. */
  readOnly?: boolean;
};

/** Shared by every partner's read-only "View vehicles" peek and "Enroll individual vehicles" picker. */
function VehiclesTable({
  providerCode,
  columns,
  testIdPrefix,
  selectedVehicleIds,
  onSelectionChange,
  onTotalChange,
  readOnly = false,
}: VehiclesTableProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('common');
  const organizationId = useOrgId();
  const [getEligibleVehicles] = useGetEligibleVehiclesMutation();
  const [search, setSearch] = useState('');
  const [fleetIds, setFleetIds] = useState<string[]>([]);
  const [retryCount, setRetryCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const searchTerm = search.trim();
  const serializedFleetIds = [...fleetIds].sort().join(',');
  const tokenCacheKey = JSON.stringify([
    organizationId,
    pagination.pageSize,
    serializedFleetIds,
    searchTerm,
  ]);
  const isUnfiltered = !searchTerm && fleetIds.length === 0;
  const pageKey = `${tokenCacheKey}:${pagination.pageIndex}:${retryCount}`;
  // Only the current search/filter's tokens are kept; changing either starts over from page 0.
  const pageTokensRef = useRef<{
    key: string;
    tokens: Map<number, string | undefined>;
  }>({ key: '', tokens: new Map() });
  const [loadedPage, setLoadedPage] = useState<{
    pageKey: string;
    rows: EligibleVehicleRow[];
    total: number;
    status: 'loading' | 'ready' | 'error';
  }>({
    pageKey: '',
    rows: [],
    total: 0,
    status: 'loading',
  });

  useEffect(() => {
    if (!organizationId) return;
    let isCurrentRequest = true;
    setLoadedPage({ pageKey, rows: [], total: 0, status: 'loading' });
    if (pageTokensRef.current.key !== tokenCacheKey) {
      pageTokensRef.current = {
        key: tokenCacheKey,
        tokens: new Map([[0, undefined]]),
      };
    }
    const pageTokens = pageTokensRef.current.tokens;

    const fetchPage = async (pageIndex: number) => {
      const continuationToken = pageTokens.get(pageIndex);
      if (pageIndex > 0 && !continuationToken) {
        throw new Error('Eligible vehicle page token is unavailable');
      }
      const response = await getEligibleVehicles({
        'cs-organization': organizationId,
        ...(continuationToken
          ? { 'cs-continuation-token': continuationToken }
          : {}),
        providerCode,
        eligibleVehiclesRequestDto: {
          itemsPerResponse: pagination.pageSize,
          ...(fleetIds.length > 0 ? { fleetIds } : {}),
          ...(searchTerm ? { search: { term: searchTerm } } : {}),
        },
      }).unwrap();
      if (!response.success || !response.result) {
        throw new Error('Unable to load eligible vehicles');
      }
      pageTokens.set(
        pageIndex + 1,
        response.result.continuation_token ?? undefined
      );
      return response.result;
    };

    void (async () => {
      try {
        for (
          let precedingPage = 0;
          precedingPage < pagination.pageIndex;
          precedingPage += 1
        ) {
          if (pageTokens.has(precedingPage + 1)) continue;
          const previousResult = await fetchPage(precedingPage);
          if (!isCurrentRequest) return;
          if (!pageTokens.get(precedingPage + 1)) {
            throw new Error('Eligible vehicle page token is unavailable');
          }
          if (!previousResult.items.length) {
            throw new Error('Eligible vehicle page is empty');
          }
        }

        const result = await fetchPage(pagination.pageIndex);
        if (!isCurrentRequest) return;
        setLoadedPage({
          pageKey,
          rows: result.items,
          total: result.total_items ?? result.items.length,
          status: 'ready',
        });
      } catch {
        if (!isCurrentRequest) return;
        setLoadedPage({ pageKey, rows: [], total: 0, status: 'error' });
      }
    })();

    return () => {
      isCurrentRequest = false;
    };
  }, [
    fleetIds,
    getEligibleVehicles,
    organizationId,
    pageKey,
    pagination.pageIndex,
    pagination.pageSize,
    providerCode,
    searchTerm,
    tokenCacheKey,
  ]);

  const rows = loadedPage.pageKey === pageKey ? loadedPage.rows : [];
  const grandTotal = loadedPage.pageKey === pageKey ? loadedPage.total : 0;
  const isFetching =
    loadedPage.pageKey !== pageKey || loadedPage.status === 'loading';
  const isLoaded =
    loadedPage.pageKey === pageKey && loadedPage.status === 'ready';
  const isLoadError =
    loadedPage.pageKey === pageKey && loadedPage.status === 'error';

  const currentPageIds = rows.map(getVehicleRowId);
  const selectedIds = readOnly ? [] : selectedVehicleIds;
  const rowSelection: MRT_RowSelectionState = useMemo(
    () => Object.fromEntries(selectedIds.map((id) => [id, true])),
    [selectedIds]
  );

  const handleRowSelectionChange: NonNullable<
    MRT_TableOptions<EligibleVehicleRow>['onRowSelectionChange']
  > = (updater) => {
    const next =
      typeof updater === 'function' ? updater(rowSelection) : updater;
    const selectedOnPage = Object.keys(next).filter(
      (id) => currentPageIds.includes(id) && next[id]
    );
    const selectedOffPage = selectedVehicleIds.filter(
      (id) => !currentPageIds.includes(id)
    );
    onSelectionChange([...selectedOffPage, ...selectedOnPage]);
  };

  const handleSearch = (input: string) => {
    setSearch(input);
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  };
  const handleFleetFilterChange = (nextFleetIds: string[]) => {
    setFleetIds(nextFleetIds);
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  };
  const formatCount = useLocalizedIntegerCount();
  const fleets = useCatalogFleets();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Reported only unfiltered, so a search can't shrink the "x/total selected" denominator.
  useEffect(() => {
    if (isLoaded && isUnfiltered) onTotalChange?.(grandTotal);
  }, [grandTotal, isLoaded, isUnfiltered, onTotalChange]);

  const FleetFilterModal = useMemo(
    () => (props: DialogComponentProps<EligibleVehiclesFilterCriteria>) => (
      <BDFilterModal
        {...props}
        filterOptions={getFleetFilterConfig(fleets)}
        defaultState={DEFAULT_FLEET_FILTER}
        type={BDFilterModalType.DROPDOWN}
      />
    ),
    [fleets]
  );

  const filterConfig = {
    component: FleetFilterModal,
    entity: { ...DEFAULT_FLEET_FILTER, fleetIds },
    defaultState: DEFAULT_FLEET_FILTER,
    id: `${testIdPrefix}-fleet-filter`,
    displayMode: isMobile
      ? TableFilterDisplayMode.MODAL
      : TableFilterDisplayMode.POPOVER,
    saveFilterOptions: (options: Partial<EligibleVehiclesFilterCriteria>) =>
      handleFleetFilterChange(options.fleetIds ?? []),
    getChipLabel: (_name: unknown, value: unknown) =>
      fleets.find((fleet) => fleet.fleetId === value)?.name ?? String(value),
  } as unknown as TableToolbarProps<unknown>['filterConfig'];

  const tableProps: MRT_TableOptions<EligibleVehicleRow> = {
    columns: columns(),
    data: rows,
    getRowId: getVehicleRowId,
    rowCount: grandTotal,
    enableRowSelection: !readOnly,
    enableSelectAll: !readOnly,
    enablePagination: true,
    enableStickyHeader: true,
    muiSelectAllCheckboxProps: {
      checked:
        currentPageIds.length > 0 &&
        currentPageIds.every((id) => selectedIds.includes(id)),
      indeterminate:
        currentPageIds.some((id) => selectedIds.includes(id)) &&
        !currentPageIds.every((id) => selectedIds.includes(id)),
      onChange: (event) => {
        const nextSelection = event.target.checked
          ? [...new Set([...selectedVehicleIds, ...currentPageIds])]
          : selectedVehicleIds.filter((id) => !currentPageIds.includes(id));
        onSelectionChange(nextSelection);
      },
    },
    // Flex column so the pagination toolbar keeps its space and TableContainer takes the rest.
    muiTablePaperProps: {
      className: classes.tablePaper,
      sx: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    muiTableContainerProps: {
      sx: { flex: 1, minHeight: 0, overflow: 'auto' },
    },
    onPaginationChange: setPagination,
    onRowSelectionChange: handleRowSelectionChange,
    state: { pagination, rowSelection, isLoading: isFetching },
  };

  return (
    <div className={classes.scrollContainer}>
      {isLoadError && (
        <AlertBanner
          variant="error"
          open
          title={t('common:errors.unableToLoad')}
          actionLabel={t('common:retry')}
          onAction={() => setRetryCount((count) => count + 1)}
          testId={`${testIdPrefix}-load-error`}
        />
      )}
      <Table
        tablePropsObj={tableProps}
        isPaginationDisabled={isFetching}
        enableMobileViewLayout
        renderAccordionMobileView
        tableToolbarConfig={{
          countLabelConfig: {
            translationKey: 'catalog:vehiclesTable.countVehicles',
            variables: {
              count: grandTotal,
              formattedCount: formatCount(grandTotal),
            },
            dataTestId: `${testIdPrefix}-count`,
          },
          searchBarConfig: {
            onSearch: (_type, input) => handleSearch(input),
            onClear: () => handleSearch(''),
            searchInput: search,
            withoutSearchTypeSelector: true,
            hideSearchByPrefix: true,
            dataTestId: `${testIdPrefix}-search`,
          },
          filterConfig,
        }}
      />
    </div>
  );
}

export default VehiclesTable;
